import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./MazeGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import MazeHighScores from "./MazeHighScores";
import { publishDemoState } from "../utils/demoMode";

const BASE_CELL = 36; // px per cell (L1)
const getMaxMazeSize = () => {
  if (typeof window === 'undefined') return 540;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Leave space for header (80px), controls (200px), and padding
  const maxWidth = Math.min(vw - 32, 600);
  const maxHeight = Math.min(vh - 320, 500);
  return Math.min(maxWidth, maxHeight);
};

// Generate a solvable "perfect maze" using recursive backtracker (depth-first search).
// Grid: 0 = path, 1 = wall
function generateMaze(size) {
  // Ensure odd size for classic maze generation
  const n = size % 2 === 0 ? size + 1 : size;
  const grid = Array.from({ length: n }, () => Array(n).fill(1));

  const dirs = [
    [0, -2], // up
    [2, 0], // right
    [0, 2], // down
    [-2, 0], // left
  ];

  const inBounds = (x, y) => x > 0 && y > 0 && x < n - 1 && y < n - 1;

  const carveFrom = (x, y) => {
    grid[y][x] = 0;
    // randomized directions
    for (const [dx, dy] of shuffle(dirs)) {
      const nx = x + dx;
      const ny = y + dy;
      if (inBounds(nx, ny) && grid[ny][nx] === 1) {
        // carve wall between
        grid[y + dy / 2][x + dx / 2] = 0;
        carveFrom(nx, ny);
      }
    }
  };

  carveFrom(1, 1);
  // Ensure start and goal are open
  grid[0][0] = 0;
  grid[1][0] = 0;
  grid[0][1] = 0;
  grid[n - 1][n - 1] = 0;
  grid[n - 2][n - 1] = 0;
  grid[n - 1][n - 2] = 0;
  return grid;
}

function shuffle(array) {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const sizeForLevel = (level) => {
  // levels: 1 -> 15, then +2 each level up to 25
  return Math.min(25, 15 + (level - 1) * 2);
};

const isWalkable = (maze, x, y) =>
  x >= 0 && y >= 0 && y < maze.length && x < maze.length && maze[y][x] === 0;

const MazeGame = ({ onPointsChange, autoStart = false }) => {
  const { t } = useLanguage();
  const { saveMazeScore, displayName, recordGameEvent } = useFirebase();
  const [level, setLevel] = useState(1);
  const [gridSize, setGridSize] = useState(sizeForLevel(1));
  const [started, setStarted] = useState(false);
  const [won, setWon] = useState(false);
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const startedAtRef = useRef(null);
  const movesRef = useRef(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const mazeRef = useRef(generateMaze(sizeForLevel(1)));
  const posRef = useRef({ x: 0, y: 0 });
  const didAutoStartRef = useRef(false);
  const startedRef = useRef(false);
  const wonRef = useRef(false);

  useEffect(() => {
    publishDemoState({
      game: "maze",
      ready: started && !won,
      won,
      pos,
    });
  }, [started, won, pos]);

  const [maxSize, setMaxSize] = useState(getMaxMazeSize);

  // Update max size on window resize
  useEffect(() => {
    const handleResize = () => setMaxSize(getMaxMazeSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate cell size to fit maze within available space
  const cellSize = useMemo(() => {
    const baseCellSize = level >= 2 ? Math.max(22, BASE_CELL - 6) : BASE_CELL;
    const idealSize = gridSize * baseCellSize;
    if (idealSize <= maxSize) return baseCellSize;
    // Scale down to fit
    return Math.floor(maxSize / gridSize);
  }, [level, gridSize, maxSize]);

  const gridStyle = useMemo(
    () => ({
      width: `${gridSize * cellSize}px`,
      height: `${gridSize * cellSize}px`,
    }),
    [gridSize, cellSize]
  );

  const START_POS = useMemo(() => ({ x: 0, y: 0 }), []);
  const GOAL_POS = useMemo(
    () => ({ x: gridSize - 1, y: gridSize - 1 }),
    [gridSize]
  );

  const regenerateMaze = useCallback(
    (nextLevel) => {
      const s = sizeForLevel(nextLevel);
      setGridSize(s);
      mazeRef.current = generateMaze(s);
    },
    []
  );

  const reset = useCallback(() => {
    setStarted(true);
    startedRef.current = true;
    setWon(false);
    wonRef.current = false;
    setMoves(0);
    setSeconds(0);
    startedAtRef.current = performance.now();
    movesRef.current = 0;
    setPos(START_POS);
    posRef.current = START_POS;
  }, [START_POS]);

  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    reset();
  }, [autoStart, reset]);

  const moveBy = useCallback((dx, dy) => {
    if (!startedRef.current || wonRef.current) return;
    const next = { x: posRef.current.x + dx, y: posRef.current.y + dy };
    if (isWalkable(mazeRef.current, next.x, next.y)) {
      posRef.current = next;
      setPos(next);
      setMoves((m) => {
        const nextMoves = m + 1;
        movesRef.current = nextMoves;
        return nextMoves;
      });
      if (next.x === GOAL_POS.x && next.y === GOAL_POS.y) {
        // Freeze the timer at exact finish time
        const elapsedMs = performance.now() - (startedAtRef.current ?? performance.now());
        const elapsedSec = Math.max(0, Math.round(elapsedMs / 100) / 10);
        setSeconds(elapsedSec);
        setWon(true);
        wonRef.current = true;
        // Persist result
        if (displayName) {
          saveMazeScore({ timeSec: elapsedSec, moves: movesRef.current, level });
        }
        recordGameEvent("maze_won", { level, timeSec: elapsedSec });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOAL_POS]);

  // Keyboard controls (arrow keys / WASD)
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = e.key;
      if (["ArrowUp","ArrowDown","ArrowLeft","ArrowRight","w","a","s","d","W","A","S","D"].includes(key)) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!startedRef.current) {
        if (key === "Enter" || key === " ") {
          e.preventDefault();
          reset();
        }
        return;
      }
      if (wonRef.current) return;
      switch (key) {
        case "ArrowUp":
        case "w":
        case "W":
          moveBy(0, -1);
          break;
        case "ArrowDown":
        case "s":
        case "S":
          moveBy(0, 1);
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          moveBy(-1, 0);
          break;
        case "ArrowRight":
        case "d":
        case "D":
          moveBy(1, 0);
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [moveBy, reset]);

  const handlePlayAgain = useCallback(() => {
    const nextLevel = level + 1;
    setLevel(nextLevel);
    regenerateMaze(nextLevel);
    setWon(false);
    wonRef.current = false;
    setMoves(0);
    setSeconds(0);
    startedAtRef.current = performance.now();
    movesRef.current = 0;
    setPos({ x: 0, y: 0 });
    posRef.current = { x: 0, y: 0 };
    setStarted(true);
    startedRef.current = true;
  }, [level, regenerateMaze]);

  // Timer effect: update seconds while started and not won
  useEffect(() => {
    if (!started || won) return;
    let rafId;
    const tick = () => {
      if (!startedAtRef.current) {
        startedAtRef.current = performance.now();
      }
      const elapsedMs = performance.now() - startedAtRef.current;
      setSeconds(Math.max(0, Math.round(elapsedMs / 100) / 10)); // one decimal
      if (!wonRef.current && startedRef.current) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [started, won]);

  return (
    <div className="maze-container">
      <div className="maze-card">
        <div className="maze-header">
          <h1 className="maze-title">🧩 {t("maze")} · L{level}</h1>
          {!won && started && <div>{t("moves")}: {moves}</div>}
        </div>
        <div className="maze-content">
          {!started && !won && (
            <div className="maze-instructions">
              <p>{t("mazeInstructions")}</p>
              <button className="maze-start-button" onClick={reset}>
                🎮 {t("startGame")}
              </button>
            </div>
          )}
          {started && !won && (
            <>
              <div className="maze-grid" style={gridStyle}>
                {mazeRef.current.map((row, y) =>
                  row.map((cell, x) => (
                    <div
                      key={`${x}-${y}`}
                      className={`maze-cell ${cell === 1 ? "wall" : "path"}`}
                      style={{
                        left: x * cellSize,
                        top: y * cellSize,
                        width: cellSize - 1,
                        height: cellSize - 1,
                        borderRadius: 4,
                      }}
                    />
                  ))
                )}
                <div
                  className="maze-start"
                  style={{
                    left: 0,
                    top: 0,
                    width: cellSize - 1,
                    height: cellSize - 1,
                  }}
                >
                  ✅
                </div>
                <div
                  className="maze-goal"
                  style={{
                    left: GOAL_POS.x * cellSize,
                    top: GOAL_POS.y * cellSize,
                    width: cellSize - 1,
                    height: cellSize - 1,
                  }}
                >
                  🎯
                </div>
                <div
                  className="maze-player"
                  style={{
                    left: pos.x * cellSize,
                    top: pos.y * cellSize,
                    width: cellSize - 1,
                    height: cellSize - 1,
                  }}
                  aria-label="Player"
                >
                  🚶
                </div>
              </div>
              <div className="maze-controls">
                <p>{t("time")} {seconds.toFixed(1)}s · {t("moves")} {moves}</p>
              </div>
              <div className="maze-mobile-controls">
                <div className="maze-mobile-row">
                  <button 
                    className="maze-mobile-btn"
                    onClick={() => moveBy(0, -1)}
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                </div>
                <div className="maze-mobile-row">
                  <button 
                    className="maze-mobile-btn"
                    onClick={() => moveBy(-1, 0)}
                    aria-label="Move left"
                  >
                    ←
                  </button>
                  <button 
                    className="maze-mobile-btn"
                    onClick={() => moveBy(0, 1)}
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button 
                    className="maze-mobile-btn"
                    onClick={() => moveBy(1, 0)}
                    aria-label="Move right"
                  >
                    →
                  </button>
                </div>
              </div>
            </>
          )}
          {won && (
            <div className="maze-win">
              <h3>🎉 {t("youWin")}</h3>
              <p>{t("finalMoves")}: {moves}</p>
              <p>{t("time")}: {seconds.toFixed(1)}s</p>
              <button className="maze-start-button" onClick={handlePlayAgain}>
                🔁 {t("playAgain")} · L{level + 1}
              </button>
              <MazeHighScores currentTimeSec={seconds} currentMoves={moves} inline />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MazeGame;


